import React, { forwardRef, useEffect, useState } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import {
  FieldWrapperVerticalV2,
  generateLocaleId,
  NumericTextField,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { Controller, useFormContext } from 'react-hook-form';
import { observer } from 'mobx-react';

// const isNumber = value => typeof value === 'number' && !Number.isNaN(value);

// null implies unlimited amount
const DEFAULT_AMOUNT = null;

const getFields = preAuthFields => {
  const defaultFields = [];

  if (preAuthFields.includes('LABOR_RATE')) {
    defaultFields.push({
      name: 'LABOR_RATE',
      label: 'Labor Rate',
    });
  }

  if (preAuthFields.includes('LABOR')) {
    defaultFields.push({
      name: 'LABOR',
      label: 'Total Labor',
    });
  }

  if (preAuthFields.includes('FREIGHT_AND_SHIPPING')) {
    defaultFields.push({
      name: 'FREIGHT_AND_SHIPPING',
      label: 'Freight & Shipping',
    });
  }

  if (preAuthFields.includes('TRAVEL')) {
    defaultFields.push({
      name: 'TRAVEL',
      label: 'Travel',
    });
  }

  if (preAuthFields.includes('PARTS_TOTAL')) {
    defaultFields.push({
      name: 'PARTS_TOTAL',
      label: 'Total Parts',
    });
  }

  if (preAuthFields.includes('PARTS_HANDLING')) {
    defaultFields.push({
      name: 'PARTS_HANDLING',
      label: 'Parts Handling',
    });
  }

  if (preAuthFields.includes('TAX')) {
    defaultFields.push({
      name: 'TAX',
      label: 'Tax',
    });
  }

  if (preAuthFields.includes('OTHER')) {
    defaultFields.push({
      name: 'OTHER',
      label: 'Other',
    });
  }

  if (preAuthFields.includes('TOTAL_AUTHORIZED')) {
    defaultFields.push({
      name: 'TOTAL_AUTHORIZED',
      label: 'Total',
      offset: 1,
    });
  }

  return defaultFields;
};

const getFieldValue = (value, forceValue = null) => {
  if (value === 0) {
    return 0;
  }
  if (value) {
    return value;
  }
  if (forceValue === 0) {
    return 0;
  }
  return DEFAULT_AMOUNT;
};

const getControlValues = (defaultFields, values) => {
  const received = values.reduce((acc, item) => {
    return {
      ...acc,
      [item?.amountType]: item.amountAuthorized,
    };
  }, {});

  let finalData = {};
  defaultFields.forEach(item => {
    finalData = {
      ...finalData,
      [item?.name]: getFieldValue(received?.[item?.name]),
    };
  });
  return finalData;
};

const getUnlimitedValues = (defaultFields, valuesAsObect) => {
  return defaultFields.reduce((acc, item) => {
    return {
      ...acc,
      [item.name]:
        valuesAsObect?.[item?.name] === null ||
        valuesAsObect?.[item?.name] === undefined,
    };
  }, {});
};

export const getApiRequestFormat = (controlValues, newValueObj) => {
  const finalRequestData = Object.entries(controlValues).reduce((acc, item) => {
    const [key, value] = item;
    acc.push({
      amountType: key,
      amountAuthorized: newValueObj?.key === key ? newValueObj?.value : value,
    });
    return acc;
  }, []);
  return finalRequestData;
};

const PreauthorizationForm = forwardRef((props, ref) => {
  const { control, watch, getValues } = useFormContext();

  if (!Array.isArray(props?.field?.preAuthFields)) {
    throw new Error(
      'Please Add PreAuth Fields Array for PreAuthorization Component e.g ["totalLabor"]',
    );
  }

  const [controlValues, setControlValues] = useState({});
  const [initControlValues, setInitControlValues] = useState({});
  const [unlimited, setUnlimited] = useState({});

  const currentValues = watch(props?.name);
  const formFields = getFields(props?.field?.preAuthFields);

  useEffect(() => {
    setInitControlValues(getControlValues(formFields, getValues(props?.name)));
  }, []);

  useEffect(() => {
    setControlValues(getControlValues(formFields, currentValues));
  }, [JSON.stringify(currentValues)]);

  useEffect(() => {
    setUnlimited(getUnlimitedValues(formFields, controlValues));
  }, [JSON.stringify(controlValues)]);

  return (
    <Box ref={ref}>
      <Controller
        control={control}
        name={props?.name}
        render={({ field: controllerField }) => {
          return (
            <Grid2 container spacing={2}>
              {formFields.map(defaultField => {
                const fieldName = `${props.name}.${defaultField.name}`;

                return (
                  <Grid2
                    xs={12}
                    md={1.5}
                    {...(defaultField.offset ? { mdOffset: 'auto' } : {})}
                    key={defaultField.name}>
                    <FieldWrapperVerticalV2
                      key={fieldName}
                      field={{
                        name: fieldName,
                        label: {
                          localeId: generateLocaleId(defaultField.label),
                          defaultMessage: defaultField.label,
                        },
                      }}>
                      {!unlimited[defaultField.name] ? (
                        <NumericTextField
                          decimalScale={2}
                          fixedDecimalScale
                          allowNull
                          positiveNumber
                          startAdornment="$"
                          isCurrency
                          name={fieldName}
                          permission={props?.permission}
                          readOnlyValue={getFieldValue(
                            controlValues[defaultField?.name],
                          )}
                          overwriteProps={{
                            value: getFieldValue(
                              controlValues[defaultField?.name],
                            ),
                            onChange: e => {
                              const newValueObj = {
                                key: defaultField?.name,
                                value: getFieldValue(e?.target?.value),
                              };
                              controllerField.onChange(
                                getApiRequestFormat(controlValues, newValueObj),
                              );
                            },
                          }}
                        />
                      ) : (
                        '-'
                      )}
                      {props?.permission === 'WRITE' && (
                        <div>
                          <Switch
                            size="small"
                            checked={unlimited[defaultField?.name] || false}
                            onChange={e => {
                              setUnlimited(prev => ({
                                ...prev,
                                [defaultField?.name]:
                                  e?.target?.checked || false,
                              }));
                              const newValueObj = {
                                key: defaultField?.name,
                                value: e?.target?.checked
                                  ? DEFAULT_AMOUNT
                                  : getFieldValue(
                                      initControlValues?.[defaultField?.name],
                                      0,
                                    ),
                              };
                              controllerField.onChange(
                                getApiRequestFormat(controlValues, newValueObj),
                              );
                            }}
                          />
                          <Typography component="span" variant="body2">
                            {unlimited[defaultField.name]
                              ? 'No Preset Limit'
                              : ''}
                          </Typography>
                        </div>
                      )}
                    </FieldWrapperVerticalV2>
                  </Grid2>
                );
              })}
            </Grid2>
          );
        }}
      />
    </Box>
  );
});

export default observer(PreauthorizationForm);
